import { useEffect, useState } from 'react'
import type { Discipline } from '@structural-climbing/validation'

export interface RouteFilterOptions {
  setters: { id: string; name: string }[]
  colors: string[]
  /** Easiest and hardest grade rank on the wall, or null when it has no routes. */
  grade_min: number | null
  grade_max: number | null
}

const EMPTY: RouteFilterOptions = { setters: [], colors: [], grade_min: null, grade_max: null }

/**
 * What the filter sheet can offer for a discipline: setters, hold colours and
 * the grade span in use. Read from the whole wall, not the loaded page, so a
 * paginated list still lists every setter. Only fetched while `enabled`, so the
 * bouldering tab, which doesn't show these filters, costs nothing.
 */
export function useRouteFilterOptions(
  gymId: string | null,
  apiUrl: string,
  getAccessTokenSilently: () => Promise<string>,
  discipline: Discipline,
  enabled: boolean,
): RouteFilterOptions {
  const [options, setOptions] = useState<RouteFilterOptions>(EMPTY)

  useEffect(() => {
    if (!gymId || !enabled) return
    let cancelled = false
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        const params = new URLSearchParams({ gym_id: gymId!, discipline })
        const res = await fetch(`${apiUrl}/my-gym-route-filters?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.ok && !cancelled) setOptions((await res.json()) as RouteFilterOptions)
      } catch {
        // Filters are an enhancement; the list still works without them.
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [gymId, apiUrl, getAccessTokenSilently, discipline, enabled])

  return enabled ? options : EMPTY
}
